<template>
  <div
    class="form-group"
    :class="{
      'form-group--has-error': localError && touched,
      'form-group--has-success': !localError && touched,
    }"
  >
    <label :for="id">
      {{ label }}
      <span v-if="info" class="form-group__info" v-tippy="{ content: info }">
        <BaseIcon name="info" size="14" />
      </span>
    </label>

    <!-- Trigger -->
    <div
      ref="triggerRef"
      class="multi-select__input"
      :class="{
        'multi-select__input--open': isOpen,
        'multi-select__input--disabled': disabled,
      }"
      @click="toggle"
    >
      <div class="multi-select__tags">
        <div v-for="item in selectedItems" :key="item.value" class="multi-select__tag">
          <span class="multi-select__tag-text">{{ item.label }}</span>
          <button
            class="multi-select__tag-remove"
            @click.stop="removeItem(item.value)"
            :disabled="disabled"
          >
            <BaseIcon name="xmark" size="12" />
          </button>
        </div>
        <span v-if="!selectedItems.length" class="multi-select__placeholder">
          {{ placeholder || 'Selecteer opties...' }}
        </span>
      </div>
      <div class="multi-select__arrow">
        <BaseIcon name="chevron" size="16" :class="{ 'multi-select__arrow--rotated': isOpen }" />
      </div>
    </div>

    <!-- Dropdown content (rendered into Tippy) -->
    <div ref="dropdownRef" class="multi-select__panel">
      <div v-if="searchable" class="multi-select__search" @click.stop>
        <BaseIcon name="magnify" size="14" class="multi-select__search-icon" />
        <input
          ref="searchInputRef"
          type="text"
          v-model="searchTerm"
          :placeholder="searchPlaceholder || 'Zoeken...'"
          class="multi-select__search-input"
          @keydown.escape.stop="closeDropdown"
        />
      </div>
      <div class="multi-select__options">
        <div
          v-for="option in filteredAvailableOptions"
          :key="option.value"
          class="multi-select__option"
          @click="selectOption(option.value)"
        >
          <span>{{ option.label }}</span>
        </div>

        <div
          v-if="!filteredAvailableOptions.length && !allowCreate"
          class="multi-select__no-options"
        >
          {{ searchTerm.trim() ? 'Geen resultaten' : 'Geen opties beschikbaar' }}
        </div>
      </div>

      <div v-if="allowCreate" class="multi-select__create-section">
        <div v-if="!showCreateInput" class="multi-select__create-trigger">
          <button class="multi-select__create-button" @click.stop="showCreateInput = true">
            <BaseIcon name="plus" size="14" />
            {{ createButtonText || 'Nieuwe toevoegen' }}
          </button>
        </div>
        <div v-else class="multi-select__create-form">
          <div class="multi-select__create-input">
            <input
              ref="createInputRef"
              v-model="newOptionValue"
              :placeholder="createPlaceholder || 'Nieuwe optie...'"
              @keydown.enter.prevent="createNewOption"
              @keydown.escape="cancelCreate"
              @click.stop
            />
          </div>
          <div class="multi-select__create-actions">
            <button
              class="multi-select__create-confirm"
              @click.stop="createNewOption"
              :disabled="!newOptionValue.trim()"
            >
              <BaseIcon name="check" size="12" />
            </button>
            <button class="multi-select__create-cancel" @click.stop="cancelCreate">
              <BaseIcon name="xmark" size="12" />
            </button>
          </div>
        </div>
        <button
          v-if="manageButtonText"
          type="button"
          class="multi-select__manage-button"
          @click.stop="handleManageOptions"
        >
          <BaseIcon name="edit" size="14" />
          {{ manageButtonText }}
        </button>
      </div>
    </div>

    <span v-if="localError && touched" class="form-group__error">{{ localError }}</span>
  </div>
</template>

<script>
  import { ref, watch, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
  import tippy from 'tippy.js';
  import { validateInput } from '@/utils/validation';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  export default {
    name: 'MultiSelectInput',
    components: { BaseIcon },
    props: {
      id: { type: String, required: true },
      label: { type: String, required: true },
      modelValue: { type: Array, default: () => [] },
      error: { type: String, default: '' },
      rules: { type: Array, default: () => [] },
      options: { type: Array, required: true, default: () => [] },
      placeholder: { type: String, default: 'Selecteer opties...' },
      disabled: { type: Boolean, default: false },
      info: { type: String, default: null },
      allowCreate: { type: Boolean, default: false },
      createPlaceholder: { type: String, default: 'Nieuwe optie...' },
      createButtonText: { type: String, default: 'Nieuwe toevoegen' },
      manageButtonText: { type: String, default: '' },
      valueKey: { type: String, default: 'value' },
      labelKey: { type: String, default: 'label' },
      placement: { type: String, default: 'bottom-start' },
      searchable: { type: Boolean, default: false },
      searchPlaceholder: { type: String, default: 'Zoeken...' },
    },
    emits: ['update:modelValue', 'validate', 'create-option', 'manage-options'],
    setup(props, { emit }) {
      const localError = ref(props.error);
      const touched = ref(false);
      const isOpen = ref(false);
      const showCreateInput = ref(false);
      const newOptionValue = ref('');
      const searchTerm = ref('');
      const triggerRef = ref(null);
      const dropdownRef = ref(null);
      const createInputRef = ref(null);
      const searchInputRef = ref(null);
      let tippyInstance = null;

      const selectedItems = computed(() =>
        props.modelValue
          .map((value) => {
            const option = props.options.find((opt) => opt[props.valueKey] === value);
            return option ? { value: option[props.valueKey], label: option[props.labelKey] } : null;
          })
          .filter(Boolean)
      );

      const availableOptions = computed(() =>
        props.options.filter((opt) => !props.modelValue.includes(opt[props.valueKey]))
      );

      const filteredAvailableOptions = computed(() => {
        const q = searchTerm.value.trim().toLowerCase();
        if (!props.searchable || !q) return availableOptions.value;
        return availableOptions.value.filter((opt) =>
          String(opt[props.labelKey] || '')
            .toLowerCase()
            .includes(q)
        );
      });

      const toggle = () => {
        if (props.disabled) return;
        if (tippyInstance) {
          isOpen.value ? tippyInstance.hide() : tippyInstance.show();
        }
      };

      const closeDropdown = () => {
        tippyInstance?.hide();
        showCreateInput.value = false;
        newOptionValue.value = '';
        searchTerm.value = '';
      };

      const selectOption = (value) => {
        if (!props.modelValue.includes(value)) {
          emit('update:modelValue', [...props.modelValue, value]);
          touched.value = true;
          validate();
        }
      };

      const removeItem = (value) => {
        emit(
          'update:modelValue',
          props.modelValue.filter((v) => v !== value)
        );
        touched.value = true;
        validate();
      };

      const createNewOption = () => {
        const trimmedValue = newOptionValue.value.trim();
        if (!trimmedValue) return;

        const value = trimmedValue.toLowerCase().replace(/\s+/g, '-');

        // If the generated slug already exists as an option, select it
        // instead of re-creating a duplicate (fixes slug collision).
        const existing = props.options.find((opt) => opt[props.valueKey] === value);
        if (existing) {
          selectOption(value); // selectOption dedupes against modelValue
        } else {
          const newOption = {
            [props.valueKey]: value,
            [props.labelKey]: trimmedValue,
          };
          emit('create-option', newOption);
          if (!props.modelValue.includes(value)) {
            emit('update:modelValue', [...props.modelValue, value]);
          }
        }

        newOptionValue.value = '';
        showCreateInput.value = false;
        touched.value = true;
        validate();
      };

      const cancelCreate = () => {
        newOptionValue.value = '';
        showCreateInput.value = false;
      };

      const handleManageOptions = () => {
        emit('manage-options');
      };

      const validate = () => {
        const errorMessage = validateInput(props.modelValue, props.rules);
        localError.value = errorMessage;
        emit('validate', errorMessage);
      };

      watch(showCreateInput, async (val) => {
        if (val) {
          await nextTick();
          createInputRef.value?.focus();
        }
      });

      watch(
        () => props.error,
        (val) => {
          localError.value = val;
        },
        { immediate: true }
      );

      onMounted(() => {
        nextTick(() => {
          if (triggerRef.value && dropdownRef.value) {
            tippyInstance = tippy(triggerRef.value, {
              content: dropdownRef.value,
              interactive: true,
              trigger: 'manual',
              placement: props.placement,
              animation: 'shift-away',
              theme: 'dropdown',
              appendTo: () => document.body,
              maxWidth: 'none',
              onShow(instance) {
                isOpen.value = true;
                const width = triggerRef.value?.offsetWidth;
                if (width) instance.popper.style.width = `${width}px`;
              },
              onShown() {
                if (props.searchable) {
                  searchInputRef.value?.focus();
                }
              },
              onHide() {
                isOpen.value = false;
                showCreateInput.value = false;
                newOptionValue.value = '';
                searchTerm.value = '';
              },
            });
          }
        });
      });

      onBeforeUnmount(() => {
        tippyInstance?.destroy();
        tippyInstance = null;
      });

      return {
        localError,
        touched,
        isOpen,
        selectedItems,
        availableOptions,
        filteredAvailableOptions,
        searchTerm,
        showCreateInput,
        newOptionValue,
        triggerRef,
        dropdownRef,
        createInputRef,
        searchInputRef,
        toggle,
        closeDropdown,
        selectOption,
        removeItem,
        createNewOption,
        cancelCreate,
        handleManageOptions,
        validate,
      };
    },
  };
</script>

<style scoped lang="scss">
  .multi-select {
    &__input {
      display: flex;
      align-items: center;
      min-height: 42px;
      padding: 0.5rem 0.75rem;
      border: 1px solid var(--slate-300);
      border-radius: 6px;
      background: white;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover:not(&--disabled) {
        border-color: var(--slate-400);
      }

      &--open {
        border-color: var(--blue-500);
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }

      &--disabled {
        background-color: var(--slate-100);
        cursor: not-allowed;
        opacity: 0.6;
      }
    }

    &__tags {
      display: flex;
      flex-wrap: wrap;
      gap: 0.375rem;
      flex: 1;
      min-width: 0;
    }

    &__tag {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.25rem 0.5rem;
      background: var(--blue-50);
      border: 1px solid var(--blue-200);
      border-radius: 4px;
      font-size: 0.875rem;
      color: var(--blue-700);
      max-width: 200px;

      &-text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      &-remove {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        background: none;
        border: none;
        color: var(--blue-600);
        cursor: pointer;
        border-radius: 2px;
        transition: all 0.15s ease;

        &:hover:not(:disabled) {
          background: var(--blue-200);
          color: var(--blue-800);
        }

        &:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }
      }
    }

    &__placeholder {
      color: var(--slate-400);
      font-size: 0.875rem;
      user-select: none;
    }

    &__arrow {
      margin-left: 0.5rem;
      color: var(--slate-400);
      transition: transform 0.2s ease;
      transform: rotate(180deg);

      &--rotated {
        transform: rotate(0deg);
      }
    }

    &__panel {
      display: none;
      min-width: 220px;
    }

    &__search {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      padding: 0.5rem 0.75rem;
      border-bottom: 1px solid var(--slate-100);
      background: var(--slate-50);
    }

    &__search-icon {
      flex-shrink: 0;
      color: var(--slate-400);
    }

    &__search-input {
      flex: 1;
      border: none;
      background: transparent;
      outline: none;
      font-size: 0.875rem;
      color: var(--slate-700);

      &::placeholder {
        color: var(--slate-400);
      }
    }

    &__options {
      padding: 0.25rem 0;
      max-height: 320px;
      overflow-y: auto;
    }

    &__option {
      display: flex;
      align-items: center;
      padding: 0.5rem 0.75rem;
      cursor: pointer;
      font-size: 0.875rem;
      transition: background-color 0.15s ease;
      color: var(--slate-700);

      &:hover {
        background: var(--slate-50);
      }

      &:active {
        background: var(--slate-100);
      }
    }

    &__no-options {
      padding: 0.75rem;
      text-align: center;
      color: var(--slate-500);
      font-size: 0.875rem;
      font-style: italic;
    }

    &__create-section {
      border-top: 1px solid var(--slate-200);
      margin-top: 0.25rem;
      padding-top: 0.25rem;
    }

    &__create-trigger {
      padding: 0.25rem;
    }

    &__create-button {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      width: 100%;
      padding: 0.5rem 0.75rem;
      background: none;
      border: none;
      color: var(--blue-600);
      font-size: 0.875rem;
      cursor: pointer;
      border-radius: 4px;
      transition: background-color 0.15s ease;

      &:hover {
        background: var(--blue-50);
      }
    }

    &__manage-button {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      width: calc(100% - 0.5rem);
      margin: 0.25rem;
      padding: 0.5rem 0.75rem;
      background: var(--slate-50);
      border: 1px solid var(--slate-200);
      color: var(--slate-700);
      font-size: 0.875rem;
      cursor: pointer;
      border-radius: 4px;
      transition:
        background-color 0.15s ease,
        border-color 0.15s ease,
        color 0.15s ease;

      &:hover {
        background: var(--brand-50);
        border-color: var(--brand-300);
        color: var(--brand-700);
      }
    }

    &__create-form {
      padding: 0.5rem;
      background: var(--slate-50);
    }

    &__create-input {
      margin-bottom: 0.5rem;

      input {
        width: 100%;
        padding: 0.5rem;
        border: 1px solid var(--slate-300);
        border-radius: 4px;
        font-size: 0.875rem;
        color: var(--slate-700);
        background-color: white;
        caret-color: var(--slate-700);

        &:focus {
          outline: none;
          border-color: var(--blue-500);
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
        }
      }
    }

    &__create-actions {
      display: flex;
      gap: 0.5rem;
      justify-content: flex-end;
    }

    &__create-confirm,
    &__create-cancel {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.15s ease;
    }

    &__create-confirm {
      background: var(--green-100);
      color: var(--green-600);

      &:hover:not(:disabled) {
        background: var(--green-200);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    &__create-cancel {
      background: var(--red-100);
      color: var(--red-600);

      &:hover {
        background: var(--red-200);
      }
    }
  }

  .form-group--has-error {
    .multi-select__input {
      border-color: var(--red-500);

      &--open {
        box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
      }
    }
  }

  .form-group--has-success {
    .multi-select__input {
      border-color: var(--green-500);

      &--open {
        box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1);
      }
    }
  }
</style>

<style lang="scss">
  .tippy-box[data-theme~='dropdown'] .multi-select__panel {
    display: block;
  }
</style>
